// api/monitoringApi.ts
import { get, post, put } from './apiClient';
import {
    ContainerApp,
    Alert,
    BulkToggleResponse,
    KubernetesClusters,
    KubernetesWorkloads,
    AKSCluster,
    PerformanceData
} from '../types/monitoring';

// Define API response interface
export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// Define DaprComponent interface
export interface DaprComponent {
    name: string;
    cluster: string;
    namespace: string;
    type: string;
    status: string;
}

/**
 * Get performance data for a cluster or all clusters
 * @param clusterName Optional cluster name to filter by
 * @returns Promise with performance data array
 */
export const getPerformanceData = (clusterName: string | undefined): Promise<ApiResponse<PerformanceData[]>> => {
    const endpoint = clusterName
        ? `/monitoring/performance/last-12h/${clusterName}`
        : '/monitoring/performance/last-12h';
    return get<ApiResponse<PerformanceData[]>>(endpoint);
};

/**
 * Get all AKS clusters
 * @returns Promise with AKS clusters array
 */
export const getAllAKSClusters = (): Promise<ApiResponse<AKSCluster[]>> => {
    return get<ApiResponse<AKSCluster[]>>('/kubernetes/aks-clusters');
};

/**
 * Get apps/workloads for a specific cluster or all clusters
 * @param clusterName Optional cluster name to filter by
 * @returns Promise with workloads array
 */
export const getClusterApps = (clusterName: string | undefined): Promise<ApiResponse<KubernetesWorkloads[]>> => {
    const endpoint = clusterName
        ? `/monitoring/clusters/${clusterName}/apps`
        : '/monitoring/workloads';
    return get<ApiResponse<KubernetesWorkloads[]>>(endpoint);
};

/**
 * Get all workloads
 * @returns Promise with workloads array
 */
export const getClusterWorkloads = (): Promise<ApiResponse<KubernetesWorkloads[]>> => {
    return get<ApiResponse<KubernetesWorkloads[]>>('/monitoring/workloads');
};

/**
 * Get Dapr components for a specific cluster or all clusters
 * @param clusterName Optional cluster name to filter by
 * @returns Promise with Dapr components array
 */
export const getDaprComponents = (clusterName: string | undefined): Promise<ApiResponse<DaprComponent[]>> => {
    const endpoint = clusterName
        ? `/monitoring/dapr-components?cluster=${clusterName}`
        : '/monitoring/dapr-components';
    return get<ApiResponse<DaprComponent[]>>(endpoint);
};

/**
 * Get all container apps with their status
 * @returns Promise with container apps and alerts
 */
export const getContainerApps = (): Promise<ApiResponse<{ containerapps: ContainerApp[], alerts: Alert[] }>> => {
    return get<ApiResponse<{ containerapps: ContainerApp[], alerts: Alert[] }>>('/monitoring/apps');
};

/**
 * Get details for a specific container app
 * @param resourceGroup Resource group name
 * @param appName Container app name
 * @returns Promise with container app details
 */
export const getContainerAppDetails = (resourceGroup: string, appName: string): Promise<ApiResponse<ContainerApp>> => {
    return get<ApiResponse<ContainerApp>>(`/monitoring/apps/${resourceGroup}/${appName}`);
};

/**
 * Restart a container app
 * @param resourceGroup Resource group name
 * @param appName Container app name
 * @returns Promise with updated container app
 */
export const restartContainerApp = (resourceGroup: string, appName: string): Promise<ApiResponse<ContainerApp>> => {
    return post<ApiResponse<ContainerApp>>(`/monitoring/apps/${resourceGroup}/${appName}/restart`, {});
};

/**
 * Toggle minScale between 0 and 1 (Always Active mode)
 * @param resourceGroup Resource group name
 * @param appName Container app name
 * @returns Promise with updated container app
 */
export const toggleMinScale = (resourceGroup: string, appName: string): Promise<ApiResponse<ContainerApp>> => {
    return post<ApiResponse<ContainerApp>>(`/monitoring/apps/${resourceGroup}/${appName}/toggle-min-scale`, {});
};

/**
 * Toggle minScale for multiple apps
 * @param apps Array of app identifiers {resourceGroup, appName}
 * @param targetState Optional target state (true for always active, false for scaling to zero)
 * @returns Promise with results for each app
 */
export const bulkToggleMinScale = (
    apps: Array<{ resourceGroup: string, appName: string }>,
    targetState?: boolean
): Promise<ApiResponse<BulkToggleResponse>> => {
    return post<ApiResponse<BulkToggleResponse>>(
        '/monitoring/apps/bulk-toggle-min-scale',
        { apps, targetState }
    );
};

/**
 * Get all alerts
 * @returns Promise with alerts
 */
export const getAlerts = (): Promise<ApiResponse<Alert[]>> => {
    return get<ApiResponse<Alert[]>>('/monitoring/alerts');
};

/**
 * Acknowledge an alert
 * @param id Alert ID
 * @returns Promise with updated alert
 */
export const acknowledgeAlert = (id: string): Promise<ApiResponse<Alert>> => {
    return put<ApiResponse<Alert>>(`/monitoring/alerts/${id}/acknowledge`, {});
};

/**
 * Get system health status summary
 * @returns Promise with health status
 */
export const getSystemHealth = (): Promise<ApiResponse<{
    healthPercentage: number;
    runningApps: number;
    totalApps: number;
    stoppedApps: number;
    criticalAlerts: number;
}>> => {
    return get<ApiResponse<{
        healthPercentage: number;
        runningApps: number;
        totalApps: number;
        stoppedApps: number;
        criticalAlerts: number;
    }>>('/monitoring/health');
};

/**
 * Get namespaces for a given cluster
 */
export const getNamespacesForCluster = (clusterName: string): Promise<ApiResponse<any[]>> => {
    return get<ApiResponse<any[]>>(`/monitoring/clusters/${clusterName}/namespaces`);
};

/**
 * Get services for a given cluster and namespace
 */
export const getServicesForClusterNamespace = (clusterName: string, namespace: string): Promise<ApiResponse<any[]>> => {
    return get<ApiResponse<any[]>>(`/monitoring/clusters/${clusterName}/services/${namespace}`);
};

/**
 * Get all namespaces across all clusters
 */
export const getAllNamespacesAcrossClusters = (): Promise<ApiResponse<any[]>> => {
    return get<ApiResponse<any[]>>('/kubernetes/namespaces/all');
};

/**
 * Get all services across all clusters and namespaces
 */
export const getAllServicesAcrossClusters = (): Promise<ApiResponse<any[]>> => {
    return get<ApiResponse<any[]>>('/kubernetes/services/all');
};